// SCR-06 people & teams and SCR-07 person detail (plan 8.4.6, 8.4.7, 7.3, 7.6). Name order by default; value sort
// only for usage/cost and only when the policy toggle is on. Small groups (< 3) are merged/hidden by the server.
// Opening a person detail is audited (person.view) and shown to that person.
import { z } from 'zod';
import { AuditKind, AuditResult, ConnectionStatus, MissingReason, ReasonCategory, RecordLevel, Tool, Provider } from '../enums.js';
import { PermissionKey, PermissionLevel } from '../permissions.js';
import {
  AppliedFilter,
  CardFooter,
  Count,
  EpochMs,
  Id,
  MethodFilter,
  PageMeta,
  PageQuery,
  PersonRef,
  RecordRef,
  RoleId,
  RoleRef,
  TeamId,
  TeamRef,
  UndoTicket,
  UserId,
  ValuedInt,
  ValuedNumber,
  Ymd,
  filterQuery,
  periodQuery,
} from '../common.js';
import { SplitRatio, SummaryFacts, TimeChart } from '../charts.js';
import { PersonApiCard, CostColumns, PersonCliCard, SmallTeamsRow, TeamCompareRow, ToolSplit } from './shared.js';
import { ConnectionView } from './connect.js';
import { PersonRecentRow } from './records.js';
import { AuditDetail } from './admin-audit.js';
import { ApprovalRow } from './admin-roles.js';

export const UserIdParams = z.object({ userId: UserId });
export const TeamIdParams = z.object({ teamId: TeamId });

/** Team comparisons retain missing states for privacy, current permissions and pending recalculation. */
const TeamRatios = z.object({ tokens: SplitRatio.nullable(), counts: SplitRatio.nullable() });
const ScopedTeamRow = TeamCompareRow.extend({
  members: Count.nullable(), active: Count.nullable(), prompts: Count.nullable(), sessions: Count.nullable(),
  byTool: ToolSplit.nullable(), ratio: TeamRatios, reason: MissingReason.optional(),
});
const ScopedSmallTeams = SmallTeamsRow.extend({ ratio: TeamRatios.nullable(), reason: MissingReason.optional() });
/** GET /api/v1/teams/compare — stats.view team+; totals never reveal a suppressed component by subtraction. */
export const TeamsCompareQuery = filterQuery({ scope: z.enum(['team', 'all']) });
export const TeamsCompareResponse = z.object({
  applied: AppliedFilter,
  rows: z.array(ScopedTeamRow),
  small: ScopedSmallTeams.nullable(),
  allSmall: z.boolean(),
  company: z.object({ users: Count.nullable(), sessions: Count.nullable(), ratio: TeamRatios, costs: CostColumns.nullable(), reason: MissingReason.optional() }),
  costVisible: z.boolean(),
  teamFilterReset: z.literal('small_group').optional(),
  footer: CardFooter.extend({ basis: Count.nullable() }),
});
export type TeamsCompareResponse = z.infer<typeof TeamsCompareResponse>;

export const PEOPLE_SORTS = ['name', 'tokens', 'cost'] as const;
/** GET /api/v1/people — people tab (people.view). `q` matches only what the viewer may see (pseudonym otherwise). */
export const PeopleListQuery = filterQuery({
  q: z.string().trim().max(60).optional(),
  sort: z.enum(PEOPLE_SORTS).default('name'),
  onlyAttention: z.enum(['0', '1']).default('0'),
  ...PageQuery.shape,
});

export const PERSON_CHIPS = ['disabled', 'pending_approval', 'reconsent', 'paused'] as const;
export const MANAGE_BLOCKS = ['perm', 'self', 'scope', 'higher'] as const;
export const ManageBlock = z.enum(MANAGE_BLOCKS);

export const PersonRow = z.object({
  person: PersonRef,
  /** Job title — only with people.identity over this person (shown and matched by `q` only then). */
  title: z.string().max(60).nullable(),
  roles: z.array(RoleRef),
  /** The team as seen on this screen: for a former member, the team they were in (record-time); else the current team. */
  team: TeamRef.nullable(),
  /** Where the person is now ("지금은 데이터팀") when it differs from `team`; else null. */
  currentTeam: TeamRef.nullable(),
  current: z.boolean(),
  /** Former member ("moved on <date> · earlier records only") or late joiner ("joined on <date> · later records only"). */
  note: z.object({ kind: z.enum(['moved', 'joined']), date: Ymd, team: TeamRef.nullable() }).nullable(),
  prompts: ValuedInt,
  tokens: ValuedNumber,
  /** Mini bar parts (tokens per tool); null under small-group masking. */
  byTool: ToolSplit.nullable(),
  costs: z.object({ cliEstimate: ValuedNumber, unofficial: ValuedNumber, gatewayPrice: ValuedNumber }),
  mainTool: Tool.nullable(),
  lastUsedAt: EpochMs.nullable(),
  connections: z.object({ total: Count, attention: Count, worst: ConnectionStatus.nullable(), problem: z.boolean() }).nullable(),
  chips: z.array(z.enum(PERSON_CHIPS)),
  emptyReason: MissingReason.nullable(),
  canOpen: z.boolean(),
  /** Row selectable for bulk actions (people.manage within scope, not more privileged, current member). */
  canSelect: z.boolean(),
  selectBlock: ManageBlock.nullable(),
});
export type PersonRow = z.infer<typeof PersonRow>;

export const PeopleListResponse = z.object({
  applied: AppliedFilter,
  items: z.array(PersonRow),
  page: PageMeta,
  summary: z.object({ current: Count, used: Count.nullable(), former: Count, attention: Count }),
  /** An inaccessible small-team filter was cleared; the response covers the remaining permitted scope. */
  teamFilterReset: z.literal('small_group').optional(),
  /** < 3 people with records (not self-only): usage masked, cost/tool/last-used columns dropped, no selection/value sort. */
  smallGroup: z.boolean(),
  valueSortAllowed: z.boolean(),
  /** Usage and cost can have different permission/cohort scopes. Older responses use valueSortAllowed. */
  allowedValueSorts: z.array(z.enum(['tokens', 'cost'])).optional(),
  /** Bar scale over all filtered rows, not just this page. */
  maxTokens: z.number().nonnegative(),
  /** Manager team scope: people excluded as outside the scope. */
  excludedPeople: Count,
  footer: CardFooter,
});
export type PeopleListResponse = z.infer<typeof PeopleListResponse>;
/** Pseudonyms and permitted usage only; no names or text, regardless of identity permission. */
export const PeopleExportRequest = z.object({ format: z.enum(['csv', 'json']) }).strict();

/**
 * Query of the person page and its daily card: period + method only — the person comes from the path, so the
 * common `scope`/`team`/`user` keys do not apply (the server judges the person against the viewer's scope).
 */
export const PersonDetailQuery = periodQuery({ method: MethodFilter.default('all') });

/** GET /api/v1/people/:userId — person detail (people.individual). Logs person.view; out of scope → 404. */
export const PersonDetailResponse = z.object({
  person: PersonRef,
  /** true: a current member in scope · false: only past records fall in the viewer's scope (moved away). */
  current: z.boolean(),
  team: TeamRef.nullable(),
  roles: z.array(RoleRef),
  title: z.string().nullable(),
  disabledAt: EpochMs.nullable(),
  /** Status is authoritative even when the original disabling timestamp is no longer retained. */
  status: z.enum(['active', 'suspended', 'left']).optional(),
  movement: z.object({ kind: z.enum(['moved', 'joined']), date: Ymd, from: TeamRef.nullable(), to: TeamRef.nullable() }).nullable(),
  facts: z.object({
    manager: PersonRef.nullable(),
    recordLevel: RecordLevel.nullable(),
    reconsentRequired: z.boolean(),
    pause: z.object({ from: EpochMs, until: EpochMs.nullable() }).nullable(),
    /** `problem` = at least one error disconnect (not just silence). */
    connections: z.object({ total: Count, attention: Count, worst: ConnectionStatus.nullable(), problem: z.boolean() }).nullable(),
    lastUsedAt: EpochMs.nullable(),
  }),
  applied: AppliedFilter,
  /** No AI active time on a person page (plan 7.6, 8.4.7). */
  cli: PersonCliCard,
  api: PersonApiCard,
  recent: z.array(PersonRecentRow),
  connections: z.array(ConnectionView.extend({ canRevoke: z.boolean() })).nullable(),
  apiKeys: z.object({ count: Count.nullable(), reason: MissingReason.optional(), items: z.array(z.object({ provider: Provider, alias: z.string(), last4: z.string(), status: z.enum(['active', 'grace', 'revoked']) })).nullable() }),
  /** Grants (people.manage / roles.manage viewers): same time format as GrantRow; `grantedBy` null = system. */
  grants: z
    .array(z.object({ id: Id, canRevoke: z.boolean(), role: RoleRef, teams: z.array(TeamRef), grantedAt: EpochMs, grantedBy: PersonRef.nullable(), approvedBy: PersonRef.nullable(), expiresAt: EpochMs.nullable() }))
    .nullable(),
  teamHistory: z.array(z.object({ team: TeamRef, joinedAt: Ymd, leftAt: Ymd.nullable() })),
  /**
   * audit.view only: views/exports/deletions/holds about this person — latest 10 + the total. `actor` null with
   * `actorKind: 'system'` = the system; with 'person' = a name the viewer may not see (pseudonym in `actorRole`).
   */
  accessHistory: z
    .object({
      items: z.array(
        z.object({
          at: EpochMs,
          kind: AuditKind,
          result: AuditResult,
          actorKind: z.enum(['person', 'system']),
          actorRole: RoleRef.nullable(),
          actor: PersonRef.nullable(),
          reasonCategory: ReasonCategory.nullable(),
          reason: z.string().nullable(),
          record: RecordRef.nullable(),
          detail: AuditDetail.nullable(),
        }),
      ),
      total: Count,
    })
    .nullable(),
  manage: z.object({ ok: z.boolean(), block: ManageBlock.nullable() }),
  /** The person.view audit row written for this open ("이 화면을 연 기록이 남고 당사자에게 표시돼요"). */
  viewLoggedAt: EpochMs.nullable(),
});
export type PersonDetailResponse = z.infer<typeof PersonDetailResponse>;

/** GET /api/v1/people/:userId/daily — daily usage card on the person detail (stacked by method, never hourly). */
export const PersonDailyQuery = periodQuery({ method: MethodFilter.default('all'), metric: z.enum(['prompts', 'tokens', 'sessions']).default('prompts'), prev: z.enum(['0', '1']).default('0') });
export const PersonDailyResponse = z.object({ applied: AppliedFilter, chart: TimeChart, summary: SummaryFacts, footer: CardFooter });

// ───────── Admin actions on people & teams (people.manage / teams.manage; delegated admin = own teams only) ─────────

/**
 * The first role is chosen when creating or inviting (plan 7.2.1 table "처음 역할"). A role whose keys need a 2-person
 * approval starts the account as member and opens an approval (`approval` in the response); the same delegation
 * rules as grants apply (roles.manage scope, subset of the creator's permissions, expiry for text roles).
 */
const InitialRole = {
  roleId: RoleId.default('member'),
  /** Teams the role applies to (manager / delegated admin: default = the person's team). */
  roleTeamIds: z.array(TeamId).max(20).optional(),
  roleExpiresAt: Ymd.optional(),
};
/** Create an account: login id + 18-char temporary password shown once; e-mail only → Google-only account. */
export const PersonCreateRequest = z.object({
  displayName: z.string().trim().min(1).max(60),
  email: z.email().max(254),
  loginId: z.string().trim().regex(/^[a-zA-Z0-9._-]{3,64}$/).optional(),
  teamId: TeamId,
  googleOnly: z.boolean().default(false),
  ...InitialRole,
});
export const PersonCreateResponse = z.object({ person: PersonRef, tempPassword: z.string().length(18).nullable(), approval: ApprovalRow.nullable() });
export const PersonStatusResponse = z.object({ revokedTokens: Count, endedSessions: Count });
export const TempPasswordResponse = z.object({ tempPassword: z.string().length(18) });
export const MoveTeamRequest = z.object({ teamId: TeamId, reason: z.string().trim().max(200).optional() });

/** Bulk actions of people.manage. Role changes are separate (roles.manage, see RoleChangeRequest). */
export const BULK_ACTIONS = ['move_team', 'disable', 'enable'] as const;
export const BulkPeopleRequest = z
  .object({
    action: z.enum(BULK_ACTIONS),
    userIds: z.array(UserId).min(1).max(100),
    teamId: TeamId.optional(),
    reason: z.string().trim().max(200).optional(),
  })
  .superRefine((b, ctx) => {
    if (b.action === 'move_team' && !b.teamId) ctx.addIssue({ code: 'custom', message: 'teamId required', path: ['teamId'] });
  });
/** Per-person outcome (`code` = error code when skipped) and what disabling cut ("연결 n개를 끊었어요"). */
export const BulkPeopleResponse = z.object({
  results: z.array(z.object({ userId: UserId, outcome: z.enum(['done', 'skipped']), code: z.string().nullable() })),
  revokedConnections: Count,
  endedSessions: Count,
});

// ───────── Role change from the people screens (roles.manage; plan 7.2.1 table, 7.4; mockup people.js roleOutcome) ─────────

/**
 * Per-person outcome of "역할 바꾸기": now (applies at once) · needs_approval (a sensitive role is assigned → approval
 * request) · same (already exactly this role) · last_admin · disabled · self · out_of_scope · more_privileged ·
 * no_permission.
 */
export const ROLE_CHANGE_OUTCOMES = ['now', 'needs_approval', 'same', 'last_admin', 'disabled', 'self', 'out_of_scope', 'more_privileged', 'no_permission'] as const;
export const RoleChangeOutcome = z.enum(ROLE_CHANGE_OUTCOMES);
/** GET /api/v1/role-changes/grantable-roles — roles the viewer may hand out: every key within the viewer's own scope. */
export const GrantableRolesResponse = z.object({
  roles: z.array(z.object({ role: RoleRef, needsExpiry: z.boolean(), needsTeams: z.boolean(), dualApprovalKeys: z.array(PermissionKey) })),
  /** Teams the viewer may apply team-scoped roles to. */
  scopeTeams: z.array(TeamRef),
});
const RoleChangeShape = {
  userIds: z.array(UserId).min(1).max(100),
  roleId: RoleId,
  /** Manager / delegated admin: the teams (default: each person's team). Manager also becomes that team's manager. */
  teamIds: z.array(TeamId).max(20).optional(),
};
/** POST /api/v1/role-changes/preview — dry run per person (no change, no audit row). */
export const RoleChangePreviewRequest = z.object(RoleChangeShape);
export const RoleChangePreviewResponse = z.object({
  results: z.array(
    z.object({
      person: PersonRef,
      currentRoles: z.array(RoleRef),
      outcome: RoleChangeOutcome,
      /** needs_approval: the first sensitive key in the selected role and its scope. */
      approvalKey: PermissionKey.nullable(),
      approvalScope: PermissionLevel.nullable(),
    }),
  ),
  needsExpiry: z.boolean(),
  noticeImpact: z.boolean(),
});
/**
 * POST /api/v1/role-changes — REPLACES the people's roles with member + `roleId` (other grants revoked).
 * Outcomes as in the preview; needs_approval rows become approval requests (reason ≥ 10). Existing roles remain until approval.
 * Duplicate user IDs are processed once. One undo atomically restores immediate changes and withdraws still-pending requests.
 */
export const RoleChangeRequest = z.object({ ...RoleChangeShape, expiresAt: Ymd.optional(), reason: z.string().trim().max(200).optional() });
export const RoleChangeResponse = z.object({
  results: z.array(z.object({ userId: UserId, outcome: RoleChangeOutcome, approval: z.object({ id: Id, code: z.string() }).nullable() })),
  applied: Count,
  approvals: Count,
  skipped: Count,
  undo: UndoTicket.nullable(),
});

/** Invites (7 days). The code is shown once; lists show only the last group ("AIAX-••••-9P2M"). */
export const InviteRow = z.object({
  id: Id,
  email: z.string(),
  team: TeamRef,
  role: RoleRef,
  invitedBy: PersonRef,
  sentAt: EpochMs,
  expiresAt: EpochMs,
  status: z.enum(['pending', 'accepted', 'expired', 'canceled']),
  codeHint: z.string().max(8),
  acceptedAt: EpochMs.nullable(),
  user: PersonRef.nullable(),
  roleApproval: z.object({ id: Id, code: z.string(), status: ApprovalRow.shape.status, applied: z.boolean() }).nullable().optional(),
});
export const InviteListResponse = z.object({ items: z.array(InviteRow), counts: z.object({ pending: Count, accepted: Count, other: Count }) });
/** Errors: 422 domain_not_allowed (e-mail outside the allowed domains) · 409 already_invited (pending invite) · 409 name_duplicate (account exists). */
export const InviteCreateRequest = z.object({ email: z.email().max(254), teamId: TeamId, ...InitialRole });
export const InviteIssuedResponse = z.object({ invite: InviteRow, code: z.string().regex(/^AIAX-[A-HJ-NP-Z2-9]{4}-[A-HJ-NP-Z2-9]{4}$/), approval: ApprovalRow.nullable() });

/** GET /api/v1/teams — team tree for pickers. `memberCount`/`small` only for viewers with identity 'all' (small teams are not revealed). */
export const TeamNode = z.object({
  id: TeamId,
  name: z.string(),
  parentId: TeamId.nullable(),
  path: z.array(z.string()),
  group: z.boolean(),
  memberCount: Count.nullable(),
  managers: z.array(PersonRef),
  /** Administrative selection only; names retain the viewer identity scope. */
  managerAssignment: z.object({ version: z.string().regex(/^[a-f0-9]{64}$/), managers: z.array(PersonRef), pending: z.object({ id: Id, code: z.string() }).nullable() }).optional(),
  archived: z.boolean(),
  /** Can be chosen in the header team filter (small teams only for people.identity 'all'). */
  pickable: z.boolean(),
});
export const TeamsTreeResponse = z.object({ teams: z.array(TeamNode) });
export const TeamCreateRequest = z.object({ name: z.string().trim().min(1).max(40), parentId: TeamId.nullable().default(null), group: z.boolean().default(false) });
/**
 * `group` switches 본부 (group) ↔ team. To a team: only while no active team sits under it (409 conflict reason
 * team_has_children). To a group: only while nobody belongs to it and no invitation is open (409 team_has_members ·
 * team_has_invites), because people join teams, never groups.
 */
export const TeamUpdateRequest = z.object({ name: z.string().trim().min(1).max(40).optional(), parentId: TeamId.nullable().optional(), group: z.boolean().optional() });
/** Setting managers = manager role grant + team_managers together (plan 7.3.1). */
export const TeamManagersRequest = z.object({ userIds: z.array(UserId).max(10), version: z.string().regex(/^[a-f0-9]{64}$/), expiresAt: Ymd.optional(), reason: z.string().trim().max(200).optional() }).strict();
export const TeamManagersResponse = z.object({ team: TeamNode, applied: z.boolean(), approval: ApprovalRow.nullable() });

export const peopleSchemas = {
  PeopleExportRequest,
  UserIdParams,
  TeamIdParams,
  TeamsCompareQuery,
  TeamsCompareResponse,
  PeopleListQuery,
  PeopleListResponse,
  PersonDetailQuery,
  PersonDetailResponse,
  PersonDailyQuery,
  PersonDailyResponse,
  GrantableRolesResponse,
  RoleChangePreviewRequest,
  RoleChangePreviewResponse,
  RoleChangeRequest,
  RoleChangeResponse,
  PersonCreateRequest,
  PersonCreateResponse,
  PersonStatusResponse,
  TempPasswordResponse,
  MoveTeamRequest,
  BulkPeopleRequest,
  BulkPeopleResponse,
  InviteListResponse,
  InviteCreateRequest,
  InviteIssuedResponse,
  TeamsTreeResponse,
  TeamCreateRequest,
  TeamUpdateRequest,
  TeamManagersRequest,
  TeamManagersResponse,
  TeamNode,
} as const;
